import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment } from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
} from '@lib/graphql-common';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import ActionsMenu from '@lib/components/ActionsMenu/ActionsMenu';
import {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { NavItem, NavItemTypeEnum } from '@lib/components/Menu/Menu';
import Icons from '@lib/components/Icons/Icons';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { logger } from '@lib/services/logger';
import { APP_URLS } from 'constants/urls';
import { useAiChatInsert } from './AiChatInsertContext';
import { DocReferenceDocumentation } from './hooks/useDocReference';
import DocChip from './DocChip';
import CitationPageChips from './CitationPageChips';
import CitationTimeChips from './CitationTimeChips';
import getDocChipIcon, {
  isPdfAttachment,
  isVideoAttachment,
} from './utils/getDocChipIcon';
import { CitationMoment, VerifiedCitationPages } from './utils/citationPages';
import styles from './AiAssistant.module.scss';

type Props = {
  documentation: NonNullable<DocReferenceDocumentation>;
  // Verified pages this chip cites (ascending, unique) and the passages to
  // highlight on them — rendered as page chips after the chip (PDFs only),
  // each opening the viewer at that page with the passages highlighted.
  citation?: VerifiedCitationPages;
  // Verified video moments this chip cites — rendered as time chips after the
  // chip (videos only), each opening the player there with the moment marked.
  moments?: CitationMoment[];
};

// Stable NavItem leftIcon renderers — kept at module scope so they aren't
// recreated each render (react/no-unstable-nested-components). The built-in
// Icons.* helpers are already module-level; these cover icons not in that set.
const renderBookmarkAddIcon = () => (
  <MaterialIcon icon="bookmark_add" symbolsOutlined />
);
const renderBookmarkRemoveIcon = () => (
  <MaterialIcon icon="bookmark_remove" symbolsOutlined />
);
const renderInsertIcon = () => <MaterialIcon icon="reply" symbolsOutlined />;

// Resolved inline `:documentation{id}` reference: a compact document chip with a
// trailing menu mirroring the block preview's actions (View document / Version
// notes / Details / favourite toggle / Insert into chat). Rendered only once the
// reference resolves — pending/unavailable states stay as the muted, menu-less
// chip in DocReference.
function InlineDocChip({ documentation, citation, moments }: Props) {
  const { t } = useTranslation();
  const insertIntoChat = useAiChatInsert();

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  // Page (with its highlights) or video moment the viewer opens at — set by a
  // page or time chip; the chip's own "View document" keeps opening the file
  // plainly, at the start.
  const [viewerPage, setViewerPage] = useState<number>();
  const [viewerMoment, setViewerMoment] = useState<CitationMoment>();
  const [isNotesOpen, setIsNotesOpen] = useState(false);

  const { id, name } = documentation;
  const notes = documentation.currentVersion?.notes;
  const attachment = documentation.currentVersion?.attachmentAttached as
    Attachment | null | undefined;
  const isPdf = isPdfAttachment(attachment);
  const isVideo = isVideoAttachment(attachment);
  const citationPages = citation?.pages;
  const isFavorited = documentation.favorited ?? false;
  const hasNotes = hasTextEditorValue(notes || undefined);

  const label = name || t('document');
  const filePermissions = getDocumentationFilePermissions(documentation);

  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });
  const isFavoriteLoading = favoriteLoading || unfavoriteLoading;

  const onFavoriteToggle = useCallback(() => {
    const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
    const successMessage = isFavorited
      ? t('remove-from-favorites-success')
      : t('add-to-favorites-success');
    mutation({
      variables: { ids: [id] },
      update: (cache) => {
        cache.modify({
          id: cache.identify(documentation),
          fields: { favorited: () => !isFavorited },
        });
      },
      onCompleted: () => toast({ content: successMessage }),
    });
  }, [bulkFavorite, bulkUnfavorite, documentation, id, isFavorited, t]);

  const onDetails = useCallback(() => {
    const to = APP_URLS.dashboard.documentation.view.getDynamicPath({
      pathParts: { id },
    });
    window.open(to, '_blank')?.focus();
  }, [id]);

  const onOpenPage = useCallback((page: number) => {
    setViewerPage(page);
    setViewerMoment(undefined);
    setIsViewerOpen(true);
  }, []);

  const onOpenMoment = useCallback((moment: CitationMoment) => {
    setViewerPage(undefined);
    setViewerMoment(moment);
    setIsViewerOpen(true);
  }, []);

  const hasHiddenCitations =
    (!!citationPages?.length && !isPdf) || (!!moments?.length && !isVideo);
  useEffect(() => {
    if (hasHiddenCitations) {
      logger.debug('AiAssistant: verified citation on a mismatched file type', {
        id,
        versionId: documentation.currentVersion?.id,
      });
    }
  }, [documentation.currentVersion?.id, hasHiddenCitations, id]);

  const onInsertIntoChat = useCallback(() => {
    insertIntoChat({ id, name: label });
  }, [id, label, insertIntoChat]);

  // ActionsMenu's Popover auto-closes when a menu item is clicked, so the
  // handlers stay plain (no explicit close needed).
  const actions: NavItem<never>[] = useMemo(
    () => [
      {
        id: 'view-document',
        title: t('view-document'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: Icons.view,
        onClick: () => {
          setViewerPage(undefined);
          setViewerMoment(undefined);
          setIsViewerOpen(true);
        },
      },
      {
        id: 'version-notes',
        title: t('version-notes'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: Icons.article,
        disabled: !hasNotes,
        onClick: () => setIsNotesOpen(true),
      },
      {
        id: 'details',
        title: t('details'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: Icons.description,
        onClick: onDetails,
      },
      {
        id: 'favorite',
        title: isFavorited ? t('remove-from-favorites') : t('add-to-favorites'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: isFavorited
          ? renderBookmarkRemoveIcon
          : renderBookmarkAddIcon,
        onClick: onFavoriteToggle,
      },
      {
        id: 'insert-into-chat',
        title: t('insert-into-chat'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: renderInsertIcon,
        onClick: onInsertIntoChat,
      },
    ],
    [hasNotes, isFavorited, onDetails, onFavoriteToggle, onInsertIntoChat, t],
  );

  return (
    <>
      <DocChip
        label={label}
        icon={getDocChipIcon(attachment)}
        trailing={
          <ActionsMenu
            actions={actions}
            icon="more_vert"
            iconButtonSize={IconButtonSizes.Small}
            iconButtonType={IconButtonTypes.SecondaryFilled}
            iconButtonClassName={styles.docRefChipMenuBtn}
            iconButtonRounded
          />
        }
      />
      {!!citationPages?.length && isPdf && (
        <CitationPageChips pages={citationPages} onOpenPage={onOpenPage} />
      )}
      {!!moments?.length && isVideo && (
        <CitationTimeChips moments={moments} onOpenMoment={onOpenMoment} />
      )}
      <FileViewerModal
        attachment={attachment}
        attachmentName={name || undefined}
        documentationVersionId={documentation.currentVersion?.id}
        initialPage={viewerPage}
        // Highlights belong to the page chips only — "View document" opens
        // the file plainly.
        highlights={viewerPage ? citation?.highlights : undefined}
        initialTime={viewerMoment?.startSeconds}
        markedRange={
          viewerMoment
            ? { start: viewerMoment.startSeconds, end: viewerMoment.endSeconds }
            : undefined
        }
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        useDownloadTool={filePermissions.allowDownload}
        usePrintTool={filePermissions.allowPrint}
        useFavoriteTool
        onFavoriteToggle={onFavoriteToggle}
        isFavorited={isFavorited}
        isFavoriteLoading={isFavoriteLoading}
      />
      {isNotesOpen && hasNotes && (
        <DocumentVersionNotes
          isOpen={isNotesOpen}
          notes={notes || ''}
          onClose={() => setIsNotesOpen(false)}
        />
      )}
    </>
  );
}

export default InlineDocChip;
